import { useMemo, type ReactElement } from 'react'
import { isEmpty } from '@terros/common'
import { MultiSelect, Select, type ComboboxData } from '@mantine/core'
import { getVariantDisplayLabel, useReportColumns, useSafeCurrentWorkflow, useTranslation } from '@front/model'

function useKpiOptions(): ComboboxData {
  const workflowContext = useSafeCurrentWorkflow()
  const workflowId = workflowContext?.workflow.id
  const { data: columns = [] } = useReportColumns(workflowId)

  return useMemo(() => {
    const groups = new Map<string, Array<{ value: string; label: string }>>()
    for (const col of columns) {
      if (col.type === 'group') continue
      const group = col.group ?? 'Other'
      const existing = groups.get(group)
      const item = { value: col.value, label: getVariantDisplayLabel(col, columns) }
      if (existing) {
        existing.push(item)
      } else {
        groups.set(group, [item])
      }
    }
    return Array.from(groups, ([group, items]) => ({ group, items }))
  }, [columns])
}

type SingleProps = {
  value: string
  onChange: (value: string) => void
  label?: string
  disabled?: boolean
}

export const KpiSingleSelect = ({ value, onChange, label, disabled }: SingleProps): ReactElement => {
  const { t } = useTranslation()
  const options = useKpiOptions()

  return (
    <Select
      label={label}
      disabled={disabled}
      data={options}
      value={value}
      onChange={(v) => {
        if (v) onChange(v)
      }}
      placeholder={t('Select metric')}
      searchable
      allowDeselect={false}
      maw={250}
    />
  )
}

type MultiProps = {
  value: string[]
  onChange: (value: string[]) => void
  label?: string
}

export const KpiMultiSelect = ({ value, onChange, label }: MultiProps): ReactElement => {
  const { t } = useTranslation()
  const options = useKpiOptions()

  return (
    <MultiSelect
      label={label}
      data={options}
      value={value}
      onChange={onChange}
      placeholder={isEmpty(value) ? t('Select metrics to compare') : undefined}
      searchable
      clearable
    />
  )
}
